import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
// import ResponsiveDataManagement from "../../../Components/ResponsiveDataManagement/ResponsiveDataManagement";
import CustomerServices from "../../../services/OrderingServices/CustomerServices";
import MobileCustomerForm from "../../Customer/Components/MobileCustomerForm";
import ResponsiveDataManagement from "../../../Components/Common/ResponsiveManagementLayout";
import { Link, useNavigate } from "react-router-dom";

const Customers = () => {
  const [customerList, setCustomerList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentPageFromTable, setCurrentPageFromTable] = useState(1);
  const [perPageFromTable, setPerPageFromTable] = useState(10);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  // Summary statistics
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalSpendings, setTotalSpending] = useState(0);
  const [topSpenderAmount, setTopSpendorAmount] = useState(0);
  const [totalDiscountAmount, setTotalDiscountAmount] = useState(0);
  const [customerTypeFilter, setCustomerTypeFilter] = useState("");

  const navigate = useNavigate();

  // Columns for Data Table
  const columns = [
    {
      name: "S.N",
      selector: (row, index) =>
        (currentPageFromTable - 1) * perPageFromTable + index + 1,
      width: "75px",
      sortable: false,
    },
    {
      name: "Name",
      selector: (row) => `${row.firstName} ${row.lastName}`,
      cell: (row) => (
        <Link to={`/customer/${row?.id}`}>
          {row.firstName} {row.lastName}
        </Link>
      ),
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Phone No",
      selector: (row) => row.phoneNo,
      sortable: true,
    },
    {
      name: "Customer Type",
      selector: (row) => row.customerTypeName || "-",
      sortable: true,
    },
    {
      name: "Discount (%)",
      selector: (row) => row.customerTypeDiscount || 0,
      sortable: true,
      right: true,
    },
  ];

  // Stats cards
  const stats = [
    {
      title: "Total Customers",
      value: totalCustomers,
      color: "#0d6efd",
      icon: "bx bx-user",
    },
    {
      title: "Total Spendings",
      value: totalSpendings,
      color: "#28a745",
      icon: "bx bx-purchase-tag",
    },
    {
      title: "Top Spendor Amount",
      value: topSpenderAmount,
      color: "#17a2b8",
      icon: "bx bx-category",
    },
    {
      title: "Total Discount Amount",
      value: totalDiscountAmount,
      color: "#ffc107",
      icon: "bx bx-dollar",
    },
  ];

  // Fetch customers API
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await CustomerServices.getCustomerLists();
      setCustomerList(response.customers || []);

      setTotalCustomers(response.summary?.totalCustomers || 0);
      setTotalSpending(response.summary?.totalSpendings || 0);
      setTopSpendorAmount(response.summary?.topSpenderAmount || 0);
      setTotalDiscountAmount(response.summary?.totalDiscountAmount || 0);

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Handlers
  const handleView = (customer) => {
    // Navigate to the customer page
    navigate(`/customer/${customer.id}`);
  };
  const handleAdd = () => setAddModal(true);
  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setAddModal(true);
  };

  const handleDelete = async (customer) => {
    try {
      await CustomerServices.delete(customer);
      setCustomerList((prev) => prev.filter((c) => c.id !== customer.id));
    } catch (error) {}
  };

  const filteredCustomerList = customerList.filter((item) => {
    const matchesCustomerType = customerTypeFilter
      ? item.customerTypeName?.toLowerCase() ===
        customerTypeFilter.toLowerCase()
      : true;

    return matchesCustomerType;
  });

  const customerTypeOptions = Array.from(
    new Set(customerList.map((c) => c.customerTypeName)) // get unique names
  )
    .filter(Boolean) // remove null or undefined
    .map((typeName) => ({
      value: typeName,
      label: typeName,
    }));

  const handleCustomerTypeFilterChange = (value) => {
    setCustomerTypeFilter(value);
  };

  return (
    <>
      <ResponsiveDataManagement
        title="Customers"
        pageTitle="User Management"
        mobileCardTitle="Customers List"
        // data={customerList}
        data={filteredCustomerList.map((c) => ({
          ...c,
          onClick: () => navigate(`/customer/${c.id}`),
        }))}
        columns={columns}
        stats={stats}
        searchKeys={[
          "firstName",
          "lastName",
          "email",
          "phoneNo",
          "customerTypeName",
        ]}
        onAdd={handleAdd}
        onEdit={handleEdit}
        // onDelete={handleDelete}
        // customActions={[
        //   {
        //     label: "View",
        //     icon: "ri-eye-fill text-warning",
        //     onClick: handleView, // must receive the item
        //   },
        //   {
        //     label: "Edit",
        //     icon: "ri-edit-line text-warning",
        //     onClick: handleEdit, // must receive the item
        //   },
        // ]}
        loading={loading}
        emptyStateMessage="No customers found"
        onSelect={handleCustomerTypeFilterChange}
        selectOptions={customerTypeOptions}
        selectValue={customerTypeFilter}
        selectPlaceholder="All Customer"
        mobileCardRenderer={(item) => (
          <Card
            className="shadow-sm rounded-3 border-0 border-start border-4 border-success"
            onClick={() => handleView(item)} // Card click handler
          >
            <CardBody>
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6 className="fw-bold text-primary mb-1">
                    {item.firstName} {item.lastName}
                  </h6>

                  <p className="mb-1 text-muted small">
                    <i className="bx bx-envelope me-1" /> {item.email || "-"}
                  </p>

                  <p className="mb-2 text-muted small">
                    <i className="bx bx-phone me-1" /> {item.phoneNo || "-"}
                  </p>

                  <div className="d-flex gap-2 align-items-center flex-wrap">
                    {item.customerTypeName && (
                      <span className="badge bg-info">
                        {item.customerTypeName}
                      </span>
                    )}

                    {item.customerTypeDiscount > 0 && (
                      <span className="badge bg-success">
                        {item.customerTypeDiscount}% Discount
                      </span>
                    )}
                  </div>
                </div>

                {/* Mobile dropdown */}
                <Dropdown
                  isOpen={openDropdownId === item.id}
                  toggle={() =>
                    setOpenDropdownId(
                      openDropdownId === item.id ? null : item.id
                    )
                  }
                  direction="down"
                >
                  <DropdownToggle
                    color="light"
                    className="btn-sm rounded-circle shadow-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="ri-more-2-fill" />
                  </DropdownToggle>

                  <DropdownMenu end>
                    <DropdownItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleView(item);
                        setOpenDropdownId(null);
                      }}
                    >
                      <i className="ri-eye-fill me-2" />
                      View
                    </DropdownItem>

                    <DropdownItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(item);
                        setOpenDropdownId(null);
                      }}
                    >
                      <i className="ri-edit-line me-2" />
                      Edit
                    </DropdownItem>

                    {/* <DropdownItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item);
                        setOpenDropdownId(null);
                      }}
                    >
                      <i className="ri-delete-bin-line me-2" />
                      Delete
                    </DropdownItem> */}
                  </DropdownMenu>
                </Dropdown>
              </div>
            </CardBody>
          </Card>
        )}
        onPaginationChange={({ page, perPage }) => {
          setCurrentPageFromTable(page); // save current page
          setPerPageFromTable(perPage); // save rows per page
        }}
      />

      {/* Mobile Add/Edit Form */}
      <MobileCustomerForm
        modal={addModal}
        toggleModal={() => {
          setAddModal(false);
          setSelectedCustomer(null);
        }}
        setCustomerList={setCustomerList}
        customerList={customerList}
        selectedCustomer={selectedCustomer}
        fetchCustomers={fetchCustomers}
      />
    </>
  );
};

export default Customers;
